import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { groupedTextRegistry, TEXT_REGISTRY } from "@/cms/textRegistry";

/** Flat key/value editor for every string in textRegistry.js — the "Text
 * & Labels" entry in the Edit Site page picker. Unlike the block CMS,
 * there's no per-page layout here: every registered string is one row,
 * grouped by the flow it belongs to, with a live search filter since the
 * list only grows as more of the app gets wired up to useSiteText(). Same
 * draft/publish shape and buttons as the block editor for consistency.
 * @param {{
 *   values: Record<string, string>;
 *   onChange: (next: Record<string, string>) => void;
 * }} props
 */
export default function StringsEditor({ values, onChange }) {
  const [query, setQuery] = useState("");
  const groups = useMemo(() => groupedTextRegistry(), []);

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map(({ group, items }) => ({
        group,
        items: items.filter(
          (item) =>
            item.label.toLowerCase().includes(q) ||
            item.key.toLowerCase().includes(q) ||
            (values[item.key] || item.defaultValue).toLowerCase().includes(q),
        ),
      }))
      .filter(({ items }) => items.length > 0);
  }, [groups, query, values]);

  const updateValue = (key, value) => {
    onChange({ ...values, [key]: value });
  };

  return (
    <div className="space-y-6">
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by label, key, or current text..."
          className="w-full h-11 rounded-xl border border-gray-200 bg-white pl-10 pr-3.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-[#0B2421]/30"
        />
      </div>

      {filteredGroups.length === 0 && (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center text-gray-400 text-sm">
          No matches.
        </div>
      )}

      {filteredGroups.map(({ group, items }) => (
        <div
          key={group}
          className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
            <p className="font-semibold text-[#0B2421] text-sm">{group}</p>
          </div>
          <div className="divide-y divide-gray-100">
            {items.map((item) => (
              <div key={item.key} className="p-4">
                <label className="text-sm font-semibold text-[#0B2421] mb-1.5 block">
                  {item.label}
                </label>
                <Textarea
                  value={values[item.key] ?? item.defaultValue}
                  onChange={(e) => updateValue(item.key, e.target.value)}
                  rows={item.defaultValue.length > 80 ? 3 : 1}
                  className="rounded-xl border-gray-200 bg-white resize-none"
                />
                <p className="mt-1 text-xs text-gray-400 font-mono">
                  {item.key}
                </p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Builds the editor's starting values: every known key defaults to the
 * registry's defaultValue unless the draft/published doc already has
 * something for it — so a brand new key just added to textRegistry.js
 * shows up pre-filled instead of blank. */
export function seedStringValues(saved) {
  const seed = {};
  for (const key of Object.keys(TEXT_REGISTRY)) {
    seed[key] = saved?.[key] ?? TEXT_REGISTRY[key].defaultValue;
  }
  return seed;
}
